import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

const MRTFiltersContext = React.createContext(null);

const DATE_RANGE_YEAR_MONTH_REGEX = /^(\d{4})-(\d{2})$/;
const DATE_RANGE_MONTH_YEAR_REGEX = /^(\d{2})-(\d{4})$/;

const getColumnId = column => column?.id || column?.accessorKey || String(column?.header || '');

const normalizeFilterOption = optionValue => {
  if (typeof optionValue === 'string') {
    const value = optionValue.trim();

    if (!value) {
      return null;
    }

    return {
      label: value,
      value
    };
  }

  if (
    optionValue &&
    typeof optionValue === 'object' &&
    !Array.isArray(optionValue) &&
    optionValue.id !== null &&
    optionValue.id !== undefined &&
    optionValue.label !== null &&
    optionValue.label !== undefined
  ) {
    const value = String(optionValue.id).trim();
    const label = String(optionValue.label).trim();

    if (!value || !label) {
      return null;
    }

    return {
      label,
      value
    };
  }

  return null;
};

const buildFilterOptions = (columns, includesFilters = {}) => {
  const optionsByColumn = {};

  columns.forEach(column => {
    const columnId = getColumnId(column);

    if (column?.filterVariant === 'date-range') {
      optionsByColumn[columnId] = [];
      return;
    }

    const columnIncludes = Array.isArray(includesFilters?.[columnId])
      ? includesFilters[columnId]
      : [];

    optionsByColumn[columnId] = columnIncludes.reduce((acc, optionValue) => {
      const normalizedOption = normalizeFilterOption(optionValue);

      if (!normalizedOption) {
        return acc;
      }

      acc.push(normalizedOption);

      return acc;
    }, []);
  });

  return optionsByColumn;
};

const remapDraftValueFromOptions = (optionValues = [], filterValue) => {
  if (filterValue === null || filterValue === undefined) {
    return filterValue;
  }

  const normalizedFilterValue = String(filterValue).trim();

  if (!normalizedFilterValue) {
    return '';
  }

  const optionByValue = optionValues.find(option => option.value === normalizedFilterValue);

  if (optionByValue) {
    return optionByValue.value;
  }

  const optionByLabel = optionValues.find(option => option.label === normalizedFilterValue);

  if (optionByLabel) {
    return optionByLabel.value;
  }

  return normalizedFilterValue;
};

const createEmptyDraftValue = filterVariant => {
  if (filterVariant === 'multi-select') {
    return [];
  }

  if (filterVariant === 'date-range') {
    return { month: '', year: '' };
  }

  return '';
};

const parseDateRangeDraftValue = filterValue => {
  if (typeof filterValue !== 'string') {
    return createEmptyDraftValue('date-range');
  }

  const yearMonthMatches = filterValue.match(DATE_RANGE_YEAR_MONTH_REGEX);

  if (yearMonthMatches) {
    return {
      month: yearMonthMatches[2],
      year: yearMonthMatches[1]
    };
  }

  const monthYearMatches = filterValue.match(DATE_RANGE_MONTH_YEAR_REGEX);

  if (monthYearMatches) {
    return {
      month: monthYearMatches[1],
      year: monthYearMatches[2]
    };
  }

  return createEmptyDraftValue('date-range');
};

const buildDraftFilters = (columns, filterOptionsByColumn = {}, columnFilters = []) => {
  const filtersById = {};
  const columnMap = new Map(columns.map(column => [getColumnId(column), column]));

  columns.forEach(column => {
    filtersById[getColumnId(column)] = createEmptyDraftValue(column.filterVariant);
  });

  columnFilters.forEach(filter => {
    const column = columnMap.get(filter.id);

    if (!column) {
      return;
    }

    if (column.filterVariant === 'multi-select') {
      filtersById[filter.id] =
        typeof filter.value === 'string'
          ? filter.value
              .split(',')
              .map(value => value.trim())
              .filter(Boolean)
              .map(value =>
                remapDraftValueFromOptions(filterOptionsByColumn[filter.id] || [], value)
              )
          : [];
      return;
    }

    if (column.filterVariant === 'date-range') {
      filtersById[filter.id] = parseDateRangeDraftValue(filter.value);
      return;
    }

    filtersById[filter.id] = remapDraftValueFromOptions(
      filterOptionsByColumn[filter.id] || [],
      filter.value
    );
  });

  return filtersById;
};

export const useMRTFiltersContext = () => React.useContext(MRTFiltersContext);

const MRTFiltersProvider = memo(({ children, value = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [appliedColumnFilters, setAppliedColumnFilters] = useState([]);
  const [draftFilters, setDraftFilters] = useState({});

  const columns = useMemo(
    () => (Array.isArray(value.columns) ? value.columns : []),
    [value.columns]
  );
  const includesFilters = useMemo(
    () =>
      value?.includes?.filters && typeof value.includes.filters === 'object'
        ? value.includes.filters
        : {},
    [value?.includes?.filters]
  );
  const currentColumnFilters = useMemo(
    () => (Array.isArray(value?.state?.columnFilters) ? value.state.columnFilters : []),
    [value?.state?.columnFilters]
  );
  const onColumnFiltersChange = value?.onColumnFiltersChange;

  const filterableColumns = useMemo(
    () => columns.filter(column => Boolean(column?.filterVariant)),
    [columns]
  );

  const filterOptions = useMemo(
    () => buildFilterOptions(filterableColumns, includesFilters),
    [filterableColumns, includesFilters]
  );

  useEffect(() => {
    setAppliedColumnFilters(currentColumnFilters);
  }, [currentColumnFilters]);

  const activeFilterCount = useMemo(() => appliedColumnFilters.length, [appliedColumnFilters]);

  const isReady = useMemo(() => {
    return (
      Array.isArray(columns) &&
      filterableColumns.length > 0 &&
      Array.isArray(appliedColumnFilters) &&
      typeof onColumnFiltersChange === 'function'
    );
  }, [appliedColumnFilters, columns, filterableColumns.length, onColumnFiltersChange]);

  const openDrawer = useCallback(() => {
    if (!isReady) {
      return;
    }

    setDraftFilters(buildDraftFilters(filterableColumns, filterOptions, appliedColumnFilters));
    setIsOpen(true);
  }, [appliedColumnFilters, filterOptions, filterableColumns, isReady]);

  const closeDrawer = useCallback(() => {
    if (isReady) {
      setDraftFilters(buildDraftFilters(filterableColumns, filterOptions, appliedColumnFilters));
    } else {
      setDraftFilters({});
    }

    setIsOpen(false);
  }, [appliedColumnFilters, filterOptions, filterableColumns, isReady]);

  const setDraftFilter = useCallback((columnId, nextValue) => {
    setDraftFilters(prevState => ({
      ...prevState,
      [columnId]: nextValue
    }));
  }, []);

  const toggleDraftMultiSelectValue = useCallback((columnId, optionValue) => {
    setDraftFilters(prevState => {
      const currentValue = Array.isArray(prevState[columnId]) ? prevState[columnId] : [];
      const hasValue = currentValue.includes(optionValue);

      return {
        ...prevState,
        [columnId]: hasValue
          ? currentValue.filter(value => value !== optionValue)
          : [...currentValue, optionValue]
      };
    });
  }, []);

  const setDraftMultiSelectAll = useCallback((columnId, options = []) => {
    setDraftFilters(prevState => ({
      ...prevState,
      [columnId]: options.map(option => option.value)
    }));
  }, []);

  const clearDraftMultiSelect = useCallback(columnId => {
    setDraftFilters(prevState => ({
      ...prevState,
      [columnId]: []
    }));
  }, []);

  const clearDraft = useCallback(() => {
    if (!isReady) {
      return;
    }

    setAppliedColumnFilters([]);
    setDraftFilters(buildDraftFilters(filterableColumns, filterOptions, []));
    onColumnFiltersChange([]);
  }, [filterOptions, filterableColumns, isReady, onColumnFiltersChange]);

  const applyDraft = useCallback(() => {
    const nextColumnFilters = [];
    const validationErrors = {};

    filterableColumns.forEach(column => {
      const columnId = getColumnId(column);
      const draftValue = draftFilters[columnId];

      if (column.filterVariant === 'date-range') {
        const month = draftValue?.month?.trim?.() || '';
        const year = draftValue?.year?.trim?.() || '';

        if (!month && !year) {
          return;
        }

        if (!month || !year) {
          validationErrors[columnId] = 'Preencha mês e ano.';
          return;
        }

        nextColumnFilters.push({
          id: columnId,
          value: `${year}-${month}`
        });
        return;
      }

      if (column.filterVariant === 'multi-select') {
        if (!Array.isArray(draftValue) || draftValue.length === 0) {
          return;
        }

        nextColumnFilters.push({
          id: columnId,
          value: draftValue.join(',')
        });
        return;
      }

      if (draftValue === null || draftValue === undefined || draftValue === '') {
        return;
      }

      nextColumnFilters.push({
        id: columnId,
        value: draftValue
      });
    });

    if (Object.keys(validationErrors).length > 0) {
      return {
        success: false,
        errors: validationErrors
      };
    }

    setAppliedColumnFilters(nextColumnFilters);
    setDraftFilters(buildDraftFilters(filterableColumns, filterOptions, nextColumnFilters));
    onColumnFiltersChange(nextColumnFilters);
    setIsOpen(false);

    return {
      success: true,
      errors: {}
    };
  }, [draftFilters, filterOptions, filterableColumns, onColumnFiltersChange]);

  const contextValue = useMemo(() => {
    return {
      ...value,
      isOpen,
      isReady,
      activeFilterCount,
      draftFilters,
      filterableColumns,
      filterOptions,
      openDrawer,
      closeDrawer,
      setDraftFilter,
      toggleDraftMultiSelectValue,
      setDraftMultiSelectAll,
      clearDraftMultiSelect,
      applyDraft,
      clearDraft
    };
  }, [
    activeFilterCount,
    applyDraft,
    clearDraftMultiSelect,
    clearDraft,
    closeDrawer,
    draftFilters,
    filterOptions,
    filterableColumns,
    isOpen,
    isReady,
    openDrawer,
    setDraftMultiSelectAll,
    setDraftFilter,
    toggleDraftMultiSelectValue,
    value
  ]);

  return <MRTFiltersContext.Provider value={contextValue}>{children}</MRTFiltersContext.Provider>;
});

export default MRTFiltersProvider;
